import React, { useEffect, useState } from "react";
import styles from "./CustomerPanel.module.css";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import WebService from "../Services/WebService";
import PageLoader from "../Customs/PageLoader/PageLoader";
import CrossButton from "../Customs/Button/CrossButton";
import { FaEye, FaEyeSlash, FaEdit } from "react-icons/fa";
import EditAddressPanel from "./EditAddressPanel";

const CUSTOMER_TYPES = [
  { label: "Individual", value: "INDIVIDUAL" },
  { label: "Broker", value: "BROKER" },
  { label: "Dealer", value: "DEALER" },
];
const STATUS_OPTIONS = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
];
const PAYMENT_TERMS = [
  { label: "Monthly", value: "MONTHLY" },
  { label: "Semi-monthly", value: "SEMI_MONTHLY" },
  { label: "Weekly", value: "WEEKLY" },
];

type PanelMode = "add" | "edit";

export interface CustomerPanelProps {
  open: boolean;
  mode: PanelMode;
  userId?: number;
  onClose: () => void;
  onSaved: () => void;
}

type Country = { id: number; name: string };
type StateType = { id: number; name: string };
type City = { id: number; name: string };

interface AddressInput {
  address_type: string;
  address: string;
  landmark: string;
  country: number | "";
  state: number | "";
  city: number | "";
}

const initialAddress: AddressInput = {
  address_type: "GENERAL",
  address: "",
  landmark: "",
  country: "",
  state: "",
  city: "",
};

const initialFields = {
  username: "",
  email: "",
  mobile_number: "",
  password: "",
  user_status: "ACTIVE",
  company_name: "",
  gst_number: "",
  customer_type: "INDIVIDUAL",
  is_blocked: false,
  is_credit_allowed: false,
  credit_limit: "",
  default_discount_per: "",
  payment_term: "MONTHLY",
};

type EditAddrInitial = {
  address_id: number;
  user_id: number;
  address: string;
  landmark: string;
  country: number | "";
  state: number | "";
  city: number | "";
};

const CustomerPanel: React.FC<CustomerPanelProps> = ({
  open,
  mode,
  userId,
  onClose,
  onSaved,
}) => {
  const [fields, setFields] = useState<typeof initialFields>(initialFields);
  const [address, setAddress] = useState<AddressInput>(initialAddress);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Validation state (for task-style per-field error)
  const [submitAttempt, setSubmitAttempt] = useState(false);
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<StateType[]>([]);
  const [cities, setCities] = useState<City[]>([]);

  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [addressList, setAddressList] = useState<any[]>([]);
  const [showPassword, setShowPassword] = useState(false);

  // Slide-in EditAddressPanel
  const [editPanelOpen, setEditPanelOpen] = useState(false);
  const [editInitial, setEditInitial] = useState<EditAddrInitial | null>(null);
  const [, setEditingId] = useState<number | null>(null);
const addressListRef = React.useRef<any[]>([]);
useEffect(() => { addressListRef.current = addressList; }, [addressList]);
useEffect(() => {
  if (fields.customer_type === "INDIVIDUAL" && (fields.is_credit_allowed || fields.credit_limit)) {
    setFields(f => ({ ...f, is_credit_allowed: false, credit_limit: "" }));
  }
}, [fields.customer_type]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch countries on mount (add only)
  useEffect(() => {
    if (mode === "add" && open) {
      setLoadingCountries(true);
      WebService.getAPI<any>({ action: "/v1/door-management/users/countries" })
        .then((res) => {
          const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
          setCountries(data);
        })
        .catch(() => setCountries([]))
        .finally(() => setLoadingCountries(false));
    }
  }, [open, mode]);

  // Fetch states when country changes
  useEffect(() => {
    if (mode === "add" && address.country) {
      setLoadingStates(true);
      WebService.getAPI<any>({
        action: `/v1/door-management/users/countries/${address.country}/states`,
      })
        .then((res) => {
          const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
          setStates(data);
        })
        .catch(() => setStates([]))
        .finally(() => setLoadingStates(false));
    } else {
      setStates([]);
      setAddress((a) => ({ ...a, state: "", city: "" }));
    }
  }, [address.country, mode]);

  // Fetch cities when state changes
  useEffect(() => {
    if (mode === "add" && address.state) {
      setLoadingCities(true);
      WebService.getAPI<any>({
        action: `/v1/door-management/users/states/${address.state}/cities`,
      })
        .then((res) => {
          const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
          setCities(data);
        })
        .catch(() => setCities([]))
        .finally(() => setLoadingCities(false));
    } else {
      setCities([]);
      setAddress((a) => ({ ...a, city: "" }));
    }
  }, [address.state, mode]);

  // --- helper to fetch customer (used on open and after address save)
const fetchCustomer = async () => {
  if (!(mode === "edit" && open && userId)) return;
  setLoading(true);
  try {
    const res = await WebService.getAPI<any>({
      action: `/v1/door-management/customers?user_id=${userId}`,
    });

    // ✅ handle both shapes: { data: { users: [...] } } and { users: [...] }
    const root = res?.data?.data ?? res?.data;
    const user = root?.users?.[0];

    setFields({
      username: user?.username || "",
      email: user?.email || "",
      mobile_number: user?.mobile_number || "",
      password: "",
      user_status: user?.user_status || "ACTIVE",
      company_name: user?.company_name || "",
      gst_number: user?.gst_number || "",
      customer_type: user?.customer_type,
      is_blocked: !!user?.is_blocked,
      is_credit_allowed: !!user?.is_credit_allowed,
      credit_limit: user?.credit_limit ? String(user.credit_limit) : "",
      default_discount_per: user?.default_discount_per != null ? String(user.default_discount_per) : "",
      payment_term: user?.payment_term || "MONTHLY",
    });

    // ✅ now you’ll get complete_address populated
    setAddressList(Array.isArray(user?.address) ? user.address : []);
    setDirty(false);
  } finally {
    setLoading(false);
  }
};


  // Edit mode: fetch user details for form
  useEffect(() => {
    if (mode === "edit" && open && userId) {
      fetchCustomer();
    }
    if (mode === "add" && open) {
      setFields(initialFields);
      setAddress(initialAddress);
      setDirty(false);
    }
    setErrors({});
    setTouched({});
    setSubmitAttempt(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, open, userId]);

  const setField = <K extends keyof typeof initialFields>(key: K, value: any) => {
    setFields((f) => ({ ...f, [key]: value }));
    setDirty(true);
    setTouched((prev) => ({ ...prev, [key]: true }));
  };
  const setAddr = <K extends keyof AddressInput>(key: K, value: any) => {
    setAddress((a) => ({ ...a, [key]: value }));
    setDirty(true);
    setTouched((prev) => ({ ...prev, [key]: true }));
  };

  // --- Per-field Validation (returns errors object) ---
  const validate = (forceAll = false) => {
    let errs: { [key: string]: string } = {};

    if ((forceAll || submitAttempt || touched.username) && !fields.username.trim())
      errs.username = "Name is required";

    if ((forceAll || submitAttempt || touched.mobile_number) && !fields.mobile_number.trim())
      errs.mobile_number = "Mobile number is required";

    if (
      (forceAll || submitAttempt || touched.mobile_number) &&
      fields.mobile_number &&
      !/^\d{10}$/.test(fields.mobile_number.trim())
    )
      errs.mobile_number = "Mobile number must be 10 digits";

    if ((forceAll || submitAttempt || touched.user_status) && !fields.user_status)
      errs.user_status = "Status required";

    if ((forceAll || submitAttempt || touched.customer_type) && !fields.customer_type)
      errs.customer_type = "Customer type required";

    if ((forceAll || submitAttempt || touched.payment_term) && !fields.payment_term)
      errs.payment_term = "Payment term required";

    if (
      (fields.customer_type === "BROKER" || fields.customer_type === "DEALER") &&
      (forceAll || submitAttempt || touched.company_name) &&
      !fields.company_name.trim()
    )
      errs.company_name = "Company name required";

    if (fields.is_credit_allowed && (forceAll || submitAttempt || touched.credit_limit)) {
      const val = String(fields.credit_limit ?? "").trim();
      const num = Number(val);
      if (!val || Number.isNaN(num) || num <= 0) {
        errs.credit_limit = "Credit limit required";
      }
    }

    // ---- Default Discount % (block 100 and above) ----
    if (forceAll || submitAttempt || touched.default_discount_per) {
      const raw = fields.default_discount_per?.trim() ?? "";
      if (!raw) {
        errs.default_discount_per = "Discount % required";
      } else {
        const num = Number(raw);
        if (Number.isNaN(num)) {
          errs.default_discount_per = "Discount % must be a number";
        } else if (num < 0) {
          errs.default_discount_per = "Discount % cannot be negative";
        } else if (num >= 100) {
          errs.default_discount_per = "Discount cannot be 100% or more";
        }
      }
    }

    if (mode === "add" && (forceAll || submitAttempt || touched.password) && !fields.password.trim())
      errs.password = "Password required";

    if (mode === "add" && ((forceAll || submitAttempt || touched.address) && !address.address.trim()))
      errs.address = "Address required";

    if (mode === "add" && ((forceAll || submitAttempt || touched.landmark) && !address.landmark.trim()))
      errs.landmark = "Landmark required";

    if (mode === "add" && ((forceAll || submitAttempt || touched.country) && !address.country))
      errs.country = "Country required";

    if (mode === "add" && ((forceAll || submitAttempt || touched.state) && !address.state))
      errs.state = "State required";

    if (mode === "add" && ((forceAll || submitAttempt || touched.city) && !address.city))
      errs.city = "City required";

    return errs;
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempt(true);

    const errs = validate(true);
    setErrors(errs);

    if (Object.keys(errs).length > 0) {
      return;
    }

    setSaving(true);
    try {
      if (mode === "add") {
        const payload = {
          ...fields,
          credit_limit: fields.is_credit_allowed ? Number(fields.credit_limit) : undefined,
          default_discount_per: Number(fields.default_discount_per),
          payment_term: fields.payment_term,
          address: {
            ...address,
            country: address.country,
            state: address.state,
            city: address.city,
          },
        };
        await WebService.postAPI({
          action: `/v1/door-management/customers`,
          body: payload,
        });
        toast.success("Customer added successfully!");
      } else {
        const updatePayload: any = {
          ...fields,
          credit_limit: fields.is_credit_allowed ? Number(fields.credit_limit) : undefined,
          default_discount_per: Number(fields.default_discount_per),
          payment_term: fields.payment_term,
        };
        delete updatePayload.password;
        if (fields.customer_type === "INDIVIDUAL") {
          delete updatePayload.company_name;
          delete updatePayload.gst_number;
        }
        await WebService.patchAPI({
          action: `/v1/door-management/customers/${userId}`,
          body: updatePayload,
        });
        toast.success("Customer updated successfully!");
      }
      setDirty(false);
      onSaved();
      onClose();
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message || "Failed to save customer");
      // toast.error(typeof e === "string" ? e : e?.message || "Failed to save customer");
    }
    setSaving(false);
  };

  const handlePanelClose = () => {
    if (dirty) {
      Swal.fire({
        title: "Discard changes?",
        text: "All unsaved changes will be lost.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#FFD400",
        cancelButtonColor: "#999",
        confirmButtonText: "Yes, discard",
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          setDirty(false);
          onClose();
        }
      });
    } else {
      onClose();
    }
  };

  if (!open) return null;
  if (loading) return <PageLoader />;

  return (
    <>
      <div className={styles.overlay} tabIndex={-1} onClick={handlePanelClose}>
        <div
          className={styles.panel}
          tabIndex={0}
          onClick={(e) => e.stopPropagation()}
        >
          <CrossButton onClick={handlePanelClose} className={styles.closeBtn} />

          <h2 className={styles.heading}>
            {mode === "add" ? "Add New Customer" : "Edit Customer"}
          </h2>
          <form
            className={styles.form}
            autoComplete="off"
            onSubmit={handleSave}
            spellCheck={false}
          >
            {/* Two-column grid for all main fields */}
            <div className={styles.row}>
              <div className={styles.inputGroup}>
                <label>
                  Customer Name <span className={styles.req}>*</span>
                </label>
                <input
                  className={`${styles.input} ${errors.username ? styles.inputError : ""}`}
                  type="text"
                  value={fields.username}
                  onChange={(e) => setField("username", e.target.value)}
                  onBlur={() => setTouched((prev) => ({ ...prev, username: true }))}
                  placeholder="Customer Name"
                />
                {errors.username && <div className={styles.error}>{errors.username}</div>}
              </div>
              <div className={styles.inputGroup}>
                <label>Email</label>
                <input
                  className={styles.input}
                  type="email"
                  value={fields.email}
                  onChange={(e) => setField("email", e.target.value)}
                  placeholder="Customeremail@gmail.com"
                  
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.inputGroup}>
                <label>
                  Mobile Number <span className={styles.req}>*</span>
                </label>
                <input
                  className={`${styles.input} ${errors.mobile_number ? styles.inputError : ""}`}
                  type="text"
                  maxLength={10}
                  value={fields.mobile_number}
                  onChange={(e) =>
                    setField(
                      "mobile_number",
                      e.target.value.replace(/\D/g, "").slice(0, 10)
                    )
                  }
                  onBlur={() => setTouched((prev) => ({ ...prev, mobile_number: true }))}
                  placeholder="e.g.9876543210"
                />
                {errors.mobile_number && <div className={styles.error}>{errors.mobile_number}</div>}
              </div>
              <div className={styles.inputGroup}>
                <label>Status</label>
                <select
                  className={`${styles.select} ${errors.user_status ? styles.inputError : ""}`}
                  value={fields.user_status}
                  onChange={(e) => setField("user_status", e.target.value)}
                  onBlur={() => setTouched((prev) => ({ ...prev, user_status: true }))}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {errors.user_status && <div className={styles.error}>{errors.user_status}</div>}
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.inputGroup}>
                <label>
                  Customer Type <span className={styles.req}>*</span>
                </label>
                <select
                  className={`${styles.select} ${errors.customer_type ? styles.inputError : ""}`}
                  value={fields.customer_type}
                  onChange={(e) => setField("customer_type", e.target.value)}
                  onBlur={() => setTouched((prev) => ({ ...prev, customer_type: true }))}
                >
                  {CUSTOMER_TYPES.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {errors.customer_type && <div className={styles.error}>{errors.customer_type}</div>}
              </div>
              {fields.customer_type === "BROKER" || fields.customer_type === "DEALER" ? (
                <div className={styles.inputGroup}>
                  <label>
                    Company Name <span className={styles.req}>*</span>
                  </label>
                  <input
                    className={`${styles.input} ${errors.company_name ? styles.inputError : ""}`}
                    type="text"
                    value={fields.company_name}
                    onChange={(e) => setField("company_name", e.target.value)}
                    onBlur={() => setTouched((prev) => ({ ...prev, company_name: true }))}
                  />
                  {errors.company_name && <div className={styles.error}>{errors.company_name}</div>}
                </div>
              ) : (
                <div /> // placeholder to keep grid aligned
              )}
            </div>

            {fields.customer_type === "BROKER" || fields.customer_type === "DEALER" ? (
              <div className={styles.row}>
                <div className={styles.inputGroup}>
                  <label>GST Number</label>
                  <input
                    className={styles.input}
                    type="text"
                    value={fields.gst_number}
                    onChange={(e) => setField("gst_number", e.target.value)}
                  />
                </div>
                <div /> {/* spacer for grid */}
              </div>
            ) : null}

<div className={styles.row}>
  <div className={styles.inputGroup}>
    <label>Customer Blocked</label>
    <select
      className={styles.select}
      value={fields.is_blocked ? "true" : "false"}
      onChange={(e) => setField("is_blocked", e.target.value === "true")}
    >
      <option value="false">No</option>
      <option value="true">Yes</option>
    </select>
  </div>

  {/* Hide credit controls for INDIVIDUAL */}
  {fields.customer_type !== "INDIVIDUAL" ? (
    <div className={styles.inputGroup}>
      <label>Credit Allowed</label>
      <select
        className={styles.select}
        value={fields.is_credit_allowed ? "true" : "false"}
        onChange={(e) => setField("is_credit_allowed", e.target.value === "true")}
      >
        <option value="false">No</option>
        <option value="true">Yes</option>
      </select>
    </div>
  ) : (
    <div /> /* spacer to keep the grid aligned */
  )}
</div>

            {fields.customer_type !== "INDIVIDUAL" && fields.is_credit_allowed && (

              <div className={styles.row}>
                <div className={styles.inputGroup}>
                  <label>
                    Credit Limit <span className={styles.req}>*</span>
                  </label>
                  <input
                    className={`${styles.input} ${errors.credit_limit ? styles.inputError : ""}`}
                    type="number"
                    value={fields.credit_limit}
                    onChange={(e) => setField("credit_limit", e.target.value)}
                    onBlur={() => setTouched((prev) => ({ ...prev, credit_limit: true }))}
                    min={0}
                  />
                  {errors.credit_limit && <div className={styles.error}>{errors.credit_limit}</div>}
                </div>
                <div /> {/* spacer */}
              </div>
            )}

            <div className={styles.row}>
              <div className={styles.inputGroup}>
                <label>
                  Default Discount (%) <span className={styles.req}>*</span>
                </label>
                <input
                  className={`${styles.input} ${errors.default_discount_per ? styles.inputError : ""}`}
                  type="number"
                  value={fields.default_discount_per}
                  min={0}
                  max={100}
                  onChange={(e) => setField("default_discount_per", e.target.value)}
                  onBlur={() => setTouched((prev) => ({ ...prev, default_discount_per: true }))}
                />
                {errors.default_discount_per && (
                  <div className={styles.error}>{errors.default_discount_per}</div>
                )}
              </div>
              <div className={styles.inputGroup}>
                <label>
                  Payment Term <span className={styles.req}>*</span>
                </label>
                <select
                  className={`${styles.select} ${errors.payment_term ? styles.inputError : ""}`}
                  value={fields.payment_term}
                  onChange={(e) => setField("payment_term", e.target.value)}
                  onBlur={() => setTouched((prev) => ({ ...prev, payment_term: true }))}
                >
                  {PAYMENT_TERMS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {errors.payment_term && <div className={styles.error}>{errors.payment_term}</div>}
              </div>
            </div>

            {/* Password row (full width) */}
            <div className={styles.row}>
              <div
                className={styles.inputGroup}
                style={{ position: "relative", gridColumn: "1 / span 2" }}
              >
                <label>
                  Password <span className={styles.req}>*</span>
                </label>
                <input
                  className={`${styles.input} ${errors.password ? styles.inputError : ""}`}
                  type={showPassword ? "text" : "password"}
                  value={fields.password}
                  onChange={(e) => setField("password", e.target.value)}
                  onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
                  placeholder="Enter password"
                  style={{ paddingRight: 38 }}
                />
                <button
                  type="button"
                  className={styles.eyeBtn}
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  style={{
                    position: "absolute",
                    top: 38,
                    right: 12,
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    zIndex: 1,
                    lineHeight: 1,
                    height: 24,
                    width: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {showPassword ? <FaEyeSlash size={22} color="#333" /> : <FaEye size={22} color="#888" />}
                </button>
                {errors.password && <div className={styles.error}>{errors.password}</div>}
              </div>
            </div>

            {/* Address list - only edit */}
            {mode === "edit" && (
              <div className={styles.addressSection}>
                <label
                  style={{ fontWeight: 600, marginBottom: 8, display: "block" }}
                >
                  Addresses
                </label>

                {addressList.length === 0 ? (
                  <div
                    style={{
                      padding: "10px 12px",
                      border: "1px dashed #ccc",
                      borderRadius: 6,
                      color: "#666",
                      background: "#fafafa",
                    }}
                  >
                    No addresses found.
                  </div>
                ) : (
                  addressList.map((addr) => (
                    <div
                      key={addr.address_id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "10px 12px",
                        border: "1px solid #ddd",
                        borderRadius: 6,
                        marginBottom: 8,
                        background: "#fafafa",
                      }}
                    >
                      <div style={{ flex: 1, paddingRight: 12 }}>
                        {addr.complete_address || addr.address}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(addr.address_id); 
                          setEditInitial({
                            address_id: addr.address_id,
                            user_id: addr.user_id,
                            address: addr.address || "",
                            landmark: addr.landmark || "",
                            country: addr.country || "",
                            state: addr.state || "",
                            city: addr.city || "",
                          });
                          setEditPanelOpen(true);
                        }}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          background: "#fff",
                          border: "1px solid #ccc",
                          padding: "6px 10px",
                          borderRadius: 4,
                          cursor: "pointer",
                        }}
                        title="Edit address"
                      >
                        <FaEdit size={14} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Address section  */}
            {mode === "add" && (
              <div className={styles.addressSection}>
                <div className={styles.row}>
                  <div className={styles.inputGroup}>
                    <label>
                      Address <span className={styles.req}>*</span>
                    </label>
                    <input
                      className={`${styles.input} ${errors.address ? styles.inputError : ""}`}
                      type="text"
                      value={address.address}
                      onChange={(e) => setAddr("address", e.target.value)}
                      onBlur={() => setTouched((prev) => ({ ...prev, address: true }))}
                      placeholder="e.g Street Address"
                    />
                    {errors.address && <div className={styles.error}>{errors.address}</div>}
                  </div>
                  <div className={styles.inputGroup}>
                    <label>
                      Landmark <span className={styles.req}>*</span>
                    </label>
                    <input
                      className={`${styles.input} ${errors.landmark ? styles.inputError : ""}`}
                      type="text"
                      value={address.landmark}
                      onChange={(e) => setAddr("landmark", e.target.value)}
                      onBlur={() => setTouched((prev) => ({ ...prev, landmark: true }))}
                      placeholder="Near City Mall"
                    />
                    {errors.landmark && <div className={styles.error}>{errors.landmark}</div>}
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={styles.inputGroup}>
                    <label>Country</label>
                    <select
                      className={`${styles.select} ${errors.country ? styles.inputError : ""}`}
                      value={address.country}
                      onChange={(e) =>
                        setAddr("country", e.target.value ? Number(e.target.value) : "")
                      }
                      onBlur={() => setTouched((prev) => ({ ...prev, country: true }))}
                    >
                      <option value="">Select Country</option>
                      {loadingCountries ? (
                        <option>Loading...</option>
                      ) : (
                        countries.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.name}
                          </option>
                        ))
                      )}
                    </select>
                    {errors.country && <div className={styles.error}>{errors.country}</div>}
                  </div>
                  <div className={styles.inputGroup}>
                    <label>State</label>
                    <select
                      className={`${styles.select} ${errors.state ? styles.inputError : ""}`}
                      value={address.state}
                      onChange={(e) =>
                        setAddr("state", e.target.value ? Number(e.target.value) : "")
                      }
                      onBlur={() => setTouched((prev) => ({ ...prev, state: true }))}
                      disabled={!address.country}
                    >
                      <option value="">Select State</option>
                      {loadingStates ? (
                        <option>Loading...</option>
                      ) : (
                        states.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.name}
                          </option>
                        ))
                      )}
                    </select>
                    {errors.state && <div className={styles.error}>{errors.state}</div>}
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={styles.inputGroup} style={{ gridColumn: "1 / span 2" }}>
                    <label>City</label>
                    <select
                      className={`${styles.select} ${errors.city ? styles.inputError : ""}`}
                      value={address.city}
                      onChange={(e) => setAddr("city", e.target.value ? Number(e.target.value) : "")}
                      onBlur={() => setTouched((prev) => ({ ...prev, city: true }))}
                      disabled={!address.state}
                    >
                      <option value="">Select City</option>
                      {loadingCities ? (
                        <option>Loading...</option>
                      ) : (
                        cities.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.name}
                          </option>
                        ))
                      )}
                    </select>
                    {errors.city && <div className={styles.error}>{errors.city}</div>}
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className={styles.footerActions}>
              <button className={styles.saveBtn} type="submit" disabled={saving}>
                {saving
                  ? mode === "add"
                    ? "Adding..."
                    : "Saving..."
                  : mode === "add"
                  ? "Add Customer"
                  : "Save Changes"}
              </button>
              <button
                className={styles.cancelBtn}
                type="button"
                onClick={handlePanelClose}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Address Panel */}
{editPanelOpen && editInitial && (
  <EditAddressPanel
    open
    initial={editInitial}
    onSaved={async () => {
      try {
        await fetchCustomer();    
      } finally {
        setEditPanelOpen(false);   
        setEditInitial(null);      
       
       
      }
    }}
    onClose={() => {
      setEditPanelOpen(false);      
      setEditInitial(null);
    }}
  />
)}
    </>
  );
};

export default CustomerPanel;
